import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import dcqService from '../../services/dcqService';
import { UserService } from '../../services/userService';
import { 
  Send, 
  Loader2, 
  ChevronDown,
  Calendar, 
  Clock, 
  User,
  ChevronLeft, 
  FileText, 
  Edit, 
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Clipboard,
  Hash,
  GitBranch,
  Activity,
  Building,
  UserCheck,
  ArrowRight,
  TrendingUp,
  MoreVertical,
  Check
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import EmptyState from '../ui/EmptyState';

const SingleDCQ = () => {
  const { user: currentUser } = useContext(AuthContext);
  const { dcqId } = useParams();
  const navigate = useNavigate();
  const [dcq, setDcq] = useState(null);
  const [comments, setComments] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!dcqId) {
        setError('No DCQ ID provided');
        return;
      }

      const [dcqData, commentsData] = await Promise.all([
        dcqService.getSingleDCQ(dcqId),
        dcqService.getCommentsForDCQ(dcqId)
      ]);

      if (!dcqData) {
        setError('DCQ not found');
        return;
      }

      setDcq(dcqData);
      setComments(commentsData || []);
      setSelectedStatus(dcqData.status);

      const userIds = [...new Set((commentsData || [])
        .map(comment => {
          const userId = comment.author?.id || comment.user;
          return typeof userId === 'number' ? userId : null;
        })
        .filter(Boolean)
      )];

      if (userIds.length) {
        const userProfileMap = {};
        await Promise.all(userIds.map(async (userId) => {
          try {
            const profile = await UserService.getUserProfile(userId);
            if (profile) {
              userProfileMap[userId] = profile;
            }
          } catch (error) {
            console.warn(`Failed to fetch profile for user ${userId}:`, error);
          }
        }));
        setUserProfiles(userProfileMap);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.status === 404 
        ? 'DCQ not found. It may have been deleted or you may not have permission to view it.'
        : `An error occurred while fetching the data: ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dcqId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmittingComment) return;
  
    if (!currentUser) {
      toast.error('Please log in to add comments');
      return;
    }
  
    setIsSubmittingComment(true);
    try {
      const response = await dcqService.addComment(dcqId, newComment.trim());
      
      if (response) {
        await fetchData();
        setNewComment('');
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffTime = now - date; // Remove Math.abs to get proper direction
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Handle future dates
      if (diffTime < 0) {
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Less than a minute
      if (diffMinutes < 1) {
        return 'Just now';
      }
      
      // Less than an hour
      if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
      }
      
      // Less than a day
      if (diffHours < 24) {
        return `${diffHours}h ago`;
      }
      
      // Less than a week
      if (diffDays < 7) {
        return `${diffDays}d ago`;
      }
      
      // More than a week - show full date
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'Invalid date';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleTimeString(undefined, options);
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  };

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
  };

  const handleUpdateDCQ = async () => {
    setIsUpdatingStatus(true);
    try {
      await dcqService.updateDCQ(dcqId, { ...dcq, status: selectedStatus });
      toast.success('DCQ status updated successfully');
      await fetchData();
    } catch (error) {
      console.error('Error updating DCQ status:', error);
      toast.error('Failed to update DCQ status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const statusOptions = [
    'New',
    'Needs Clarification',
    'Waiting for Approval',
    'Verification',
    'Implementation',
    'Closed'
  ];

  // Status colours match the DCQ list: green new, amber needs clarification,
  // blue waiting, purple verification, orange implementation, gray closed.
  const getStatusColor = (status) => {
    if (!status) return 'badge-gray';
    const s = status.toLowerCase();
    if (s === 'new') return 'badge-success';
    if (s === 'needs clarification') return 'badge-warning';
    if (s === 'waiting for approval') return 'badge-info';
    if (s === 'verification') return 'bg-purple-50 text-purple-700 ring-purple-600/15 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/25';
    if (s === 'implementation') return 'bg-orange-50 text-orange-700 ring-orange-600/15 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-400/25';
    if (s === 'closed') return 'badge-gray';
    return 'badge-gray';
  };

  const getStatusIcon = (status) => {
    if (!status) return <Activity className="w-4 h-4" />;
    const s = status.toLowerCase();
    if (s === 'new') return <CheckCircle className="w-4 h-4" />;
    if (s === 'needs clarification') return <AlertTriangle className="w-4 h-4" />;
    if (s === 'waiting for approval') return <Clock className="w-4 h-4" />;
    if (s === 'verification') return <CheckCircle className="w-4 h-4" />;
    if (s === 'implementation') return <TrendingUp className="w-4 h-4" />;
    if (s === 'closed') return <Check className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const renderComment = (comment, index) => {
    const userId = comment.author?.id || comment.user?.id || comment.user;
    const userProfile = userProfiles[userId];
  
    let userName = 'Unknown User';
    if (userProfile?.first_name && userProfile?.last_name) {
      userName = `${userProfile.first_name} ${userProfile.last_name}`;
    } else if (comment.author?.email) {
      userName = comment.author.email;
    } else if (comment.user?.email) {
      userName = comment.user.email;
    } else if (typeof userId === 'number') {
      userName = `User ${userId}`;
    }
    
    const createdDate = comment.created || comment.created_at;
  
    return (
      <motion.div
        key={comment.id || index}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="group"
      >
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white dark:bg-primary-500">
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-1 flex items-center justify-between gap-3">
                <h4 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{userName}</h4>
                <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">{formatDate(createdDate)}</span>
              </div>
              <p className="break-words text-sm leading-relaxed text-gray-700 dark:text-gray-200">{comment.comment}</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center" role="status">
        <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
        <h2 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">Loading DCQ data...</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Please wait while we fetch the DCQ details</p>
      </div>
    );
  }

  // Error State
  if (error || !dcq) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="card w-full max-w-md">
          <EmptyState
            icon={AlertTriangle}
            tone="danger"
            title="Unable to Load DCQ"
            description={error || 'No DCQ data available.'}
            action={
              <button
                onClick={handleGoBack}
                className="btn btn-primary"
              >
                <ChevronLeft className="h-4 w-4" />
                Go Back
              </button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="mb-6"
      >
        <button
          onClick={handleGoBack}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to DCQs</span>
        </button>

        {/* Main header card */}
        <div className="card overflow-hidden">
          <div className="card-band">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-inset ring-white/20">
                <Hash className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-white">DCQ #{dcq.id}</h1>
                <p className="card-band-description">Data Clarification Query</p>
              </div>
            </div>
            <span className={`badge px-3 py-1 text-sm font-semibold ${getStatusColor(dcq.status)}`}>
              {getStatusIcon(dcq.status)}
              <span>{dcq.status || 'N/A'}</span>
            </span>
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 bg-gray-50 px-6 py-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            <div className="flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>{comments.length} comments</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>Created {formatDate(dcq.created_at)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column - Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* DCQ Details Card */}
          <motion.div 
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="card overflow-hidden"
          >
            <div className="p-6">
              <h2 className="mb-5 flex items-center gap-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                  <FileText className="h-5 w-5" />
                </span>
                Query Details
              </h2>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="group">
                    <div className="mb-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <GitBranch className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Type
                    </div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.type || 'N/A'}</div>
                  </div>
                  
                  <div className="group">
                    <div className="mb-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <Activity className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Action
                    </div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.action || 'N/A'}</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="group">
                    <div className="mb-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <Building className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Site
                    </div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.sitename || 'N/A'}</div>
                  </div>
                  
                  <div className="group">
                    <div className="mb-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      <UserCheck className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Participant
                    </div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{dcq.participant_display_id || dcq.participant || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Change details section */}
              <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-700">
                <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                  <Edit className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  Change Request
                </h3>
                
                <div className="space-y-4">
                  {dcq.initial_value && dcq.new_value && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900/40">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 text-sm text-gray-500 dark:text-gray-400">Initial Value</div>
                          <div className="break-words font-medium text-gray-900 dark:text-gray-100">{dcq.initial_value}</div>
                        </div>
                        <ArrowRight className="mx-4 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 text-sm text-gray-500 dark:text-gray-400">New Value</div>
                          <div className="break-words font-medium text-gray-900 dark:text-gray-100">{dcq.new_value}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {dcq.requested_change && (
                    <div>
                      <div className="mb-1 text-sm text-gray-500 dark:text-gray-400">Requested Change</div>
                      <div className="rounded-lg border border-primary-100 bg-primary-50 p-4 dark:border-primary-400/20 dark:bg-primary-400/10">
                        <p className="break-words text-sm text-gray-900 dark:text-gray-100">{dcq.requested_change}</p>
                      </div>
                    </div>
                  )}
                  
                  {dcq.reason_for_change && (
                    <div>
                      <div className="mb-1 text-sm text-gray-500 dark:text-gray-400">Reason for Change</div>
                      <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-900/40">
                        <p className="break-words text-sm text-gray-700 dark:text-gray-200">{dcq.reason_for_change}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Comments Section */}
          <motion.div 
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="card overflow-hidden"
          >
            <div className="p-6">
              <h2 className="mb-5 flex items-center justify-between text-lg font-semibold text-gray-900 dark:text-gray-100">
                <span className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                    <MessageSquare className="h-5 w-5" />
                  </span>
                  Discussion
                </span>
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{comments.length} comments</span>
              </h2>

              {/* Add comment box - always visible at top */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-500 text-sm font-semibold text-white dark:bg-gray-600">
                      {currentUser?.first_name?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <textarea
                      className="input resize-none"
                      rows={isCommentFocused ? "4" : "2"}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onFocus={() => setIsCommentFocused(true)}
                      onBlur={() => !newComment && setIsCommentFocused(false)}
                      placeholder="Share your thoughts..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <AnimatePresence>
                      {(isCommentFocused || newComment) && (
                        <motion.div 
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="mt-3 flex items-center justify-between gap-3"
                        >
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Press <kbd className="rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] dark:border-gray-700 dark:bg-gray-800">Ctrl+Enter</kbd> to submit
                          </div>
                          <button
                            className="btn btn-primary"
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || isSubmittingComment}
                          >
                            {isSubmittingComment ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Posting...
                              </>
                            ) : (
                              <>
                                <Send size={16} />
                                Post
                              </>
                            )}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Comments list */}
              <div className="space-y-4">
                {comments.length > 0 ? (
                  <AnimatePresence>
                    {comments.map((comment, index) => renderComment(comment, index))}
                  </AnimatePresence>
                ) : (
                  <EmptyState
                    icon={MessageSquare}
                    title="No comments yet"
                    description="Be the first to start the discussion"
                  />
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right column - Status and Actions */}
        <div className="space-y-6">
          {/* Status Update Card */}
          <motion.div 
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="card sticky top-8 p-6"
          >
            <h3 className="mb-4 flex items-center gap-3 font-semibold text-gray-900 dark:text-gray-100">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                <RefreshCw className="h-5 w-5" />
              </span>
              Update Status
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="label">Current Status</label>
                <div className="relative">
                  <select
                    className="input cursor-pointer appearance-none pr-10"
                    value={selectedStatus || dcq.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                </div>
              </div>
              
              <AnimatePresence>
                {selectedStatus && selectedStatus !== dcq.status && (
                  <motion.button
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    onClick={handleUpdateDCQ}
                    disabled={isUpdatingStatus}
                    className="btn btn-primary w-full"
                  >
                    {isUpdatingStatus ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Update Status
                      </>
                    )}
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Activity timeline preview */}
            <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-700">
              <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Recent Activity</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500 dark:bg-primary-400"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Status changed to <span className="font-medium text-gray-900 dark:text-gray-100">{dcq.status}</span></p>
                    <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{formatDate(dcq.updated_at)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-300">DCQ created</p>
                    <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{formatDate(dcq.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SingleDCQ;